import { WheelEvent } from 'react';
import { FormControl, FormHelperText, Grid, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReportableGeoEvent } from 'features/reports/components/ReportForm/types';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { Slider } from 'components/Slider';
import { parseCoordinateFieldValue } from 'shared/utils';
import { useDebouncedWatch } from 'hooks/useDebouncedWatch';
import { DEFAULT_LAT, DEFAULT_LON } from '../../constants';
import { MediaMap } from './MediaMap';

type MediaMapFormProps = {
  geoEvent?: ReportableGeoEvent;
  geoEventType?: GeoEventType;
  latFieldName: string;
  lngFieldName: string;
  azFieldName: string;
  allowAzimuth: boolean;
  showMap?: boolean;
  disabled?: boolean;
};

export const MediaMapForm = (props: MediaMapFormProps): JSX.Element | null => {
  const {
    geoEvent,
    geoEventType,
    latFieldName,
    lngFieldName,
    azFieldName,
    allowAzimuth,
    showMap = true,
    disabled = false,
  } = props;
  const { t } = useTranslation();
  const { control, formState } = useFormContext();

  const latitude = useDebouncedWatch<{ [latFieldName]: string }, string>({
    control,
    name: latFieldName,
    defaultValue: DEFAULT_LAT.toString(),
  });

  const longitude = useDebouncedWatch<{ [lngFieldName]: string }, string>({
    control,
    name: lngFieldName,
    defaultValue: DEFAULT_LON.toString(),
  });

  if (showMap && (!latitude || !longitude)) {
    return null;
  }

  return (
    <>
      {allowAzimuth && (
        <Grid size={12}>
          <Controller
            name={azFieldName}
            control={control}
            render={({ field, fieldState }): JSX.Element => {
              const { ref, onChange, ...muiFieldProps } = field;
              return (
                <Grid container spacing={2} sx={{ width: '100%' }}>
                  <Grid size={12}>
                    <TextField
                      id="field-control-az"
                      label={t('addIncidentReport.inputs.azimuth.label')}
                      fullWidth
                      {...muiFieldProps}
                      onChange={(e) => onChange(Number(e.target.value))}
                      inputRef={ref}
                      error={Boolean(fieldState.error)}
                      type="number"
                      disabled={disabled || formState.isSubmitting}
                      slotProps={{
                        htmlInput: {
                          pattern: '[0-9]+([.,][0-9]+)?',
                          inputMode: 'numeric',
                          onWheel: (e: WheelEvent<HTMLInputElement>) =>
                            e.currentTarget.blur(),
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={12}>
                    <FormControl fullWidth>
                      <Slider
                        value={field.value}
                        onChange={(_, newValue: number | number[]): void => {
                          const azValue =
                            typeof newValue === 'number'
                              ? newValue
                              : newValue[0];
                          field.onChange(azValue);
                        }}
                        max={360}
                        min={0}
                        disabled={disabled || formState.isSubmitting}
                      />
                      {fieldState.error?.message && (
                        <FormHelperText error>
                          {fieldState.error.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              );
            }}
          />
        </Grid>
      )}

      {showMap && (
        <Grid size={12}>
          <MediaMap
            geoEvent={geoEvent}
            geoEventType={geoEventType}
            lat={parseCoordinateFieldValue(latitude, 'latitude')}
            lng={parseCoordinateFieldValue(longitude, 'longitude')}
            azFieldName={azFieldName}
            mapId="media-location"
          />
        </Grid>
      )}
    </>
  );
};
